<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth.store';
import { config } from '@/config';
import CaseStatusBar from '@/components/aml-review/CaseStatusBar.vue';
import CaseSummaryCard from '@/components/aml-review/CaseSummaryCard.vue';
import CaseIdsPanel from '@/components/aml-review/CaseIdsPanel.vue';
import MobileBottomSheet from '@/components/aml-review/MobileBottomSheet.vue';
import ReviewerSummaryCard from '@/components/aml-review/ReviewerSummaryCard.vue';
import ShortcutsModal from '@/components/aml-review/ShortcutsModal.vue';
import HitRail from '@/components/aml-review/HitRail.vue';
import HitFilterTabs, { type HitFilterKey } from '@/components/aml-review/HitFilterTabs.vue';
import HitCategoryTag from '@/components/aml-review/HitCategoryTag.vue';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import AgentAssessmentBox from '@/components/aml-review/AgentAssessmentBox.vue';
import IdentityComparison from '@/components/aml-review/IdentityComparison.vue';
import HitRecordList from '@/components/aml-review/HitRecordList.vue';
import HitNoteBar from '@/components/aml-review/HitNoteBar.vue';
import ToolPanel from '@/components/aml-review/ToolPanel.vue';
import RecordReviewPane from '@/components/aml-review/RecordReviewPane.vue';
import MobileTopBar from '@/components/aml-review/MobileTopBar.vue';
import CaseDocumentsPanel from '@/components/aml-review/CaseDocumentsPanel.vue';
import DocumentViewerOverlay from '@/components/aml-review/DocumentViewerOverlay.vue';
import { useCaseDocuments } from '@/components/aml-review/useCaseDocuments';
import {
  fullName,
  type CaseDetail,
  type CaseHit,
  type HitVerdict,
} from '@/components/aml-review/caseDetail';
import {
  mapAmlReviewToCase,
  resolveIdentityDetails,
} from '@/components/aml-review/mapAmlReviewToCase';
import { resolveHitEnrichment } from '@/components/aml-review/resolveHitEnrichment';
import {
  loadRecordState,
  saveRecordState,
  sweepRecordState,
  type RecordStatuses,
} from '@/components/aml-review/recordStateStorage';
import { nextRecordStatus } from '@/components/aml-review/recordStatus';
import { isHitResolved } from '@/components/aml-review/hitResolution';
import {
  buildInternalEscalationSummary,
  buildPartnerEscalationSummary,
} from '@/components/aml-review/buildEscalationSummary';
import { useAmlWorkbenchShortcuts } from '@/components/aml-review/useAmlWorkbenchShortcuts';
import { useSwipeNavigation } from '@/components/aml-review/useSwipeNavigation';
import {
  pickLatestAgentReview,
  pickLatestHumanReview,
} from '@/integrations/Immersve/AmethystClient';
import type {
  AmlResultSubmissionStatus,
  CompleteAmlReviewResponse,
} from '@/integrations/Immersve/aml-review/types';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import axios from 'axios';

// This page opts out of SidebarLayout — 2a's workbench is full-bleed by
// design, with no room left for a persistent app nav once the hit rail and
// tool panel are open, so it wires its own account menu.
const authStore = useAuthStore();

const reviewerEmail = computed(() => authStore.user?.email ?? config.reviewerEmail);

const route = useRoute();
const router = useRouter();

function backToQueue() {
  if (history.state?.back) {
    router.back();
  } else {
    router.push('/aml-review');
  }
}

const loadedAmlReviewId = ref('');

const caseDetail = ref<CaseDetail | null>(null);
const loading = ref(true);
const error = ref<Error | null>(null);
const enrichmentUnavailableReason = ref<string | null>(null);

const overrides = ref<Record<string, HitVerdict>>({});
const comments = ref<Record<string, string>>({});
const reviewerSummary = ref('');
const submitted = ref(false);
const dirty = ref(false);

const agentReviewResultId = ref<string | null>(null);
const humanReviewResultId = ref<string | null>(null);
const humanReviewModifiedAt = ref<string | null>(null);
const humanReviewLastReviewedBy = ref<string | null>(null);
const humanReviewCompletedAt = ref<string | null>(null);

const saving = ref(false);
const saveError = ref<string | null>(null);
const submitting = ref(false);
const submitError = ref<string | null>(null);

const saveBanner = ref<string | null>(null);
let saveBannerTimeout: ReturnType<typeof setTimeout> | undefined;
function flashSaveBanner(message: string) {
  saveBanner.value = message;
  clearTimeout(saveBannerTimeout);
  saveBannerTimeout = setTimeout(() => {
    saveBanner.value = null;
  }, 3200);
}
onUnmounted(() => clearTimeout(saveBannerTimeout));

const rightCollapsed = ref(false);
const kbEnabled = ref(true);
const shortcutsOpen = ref(false);
const {
  documents: caseDocuments,
  unavailableReason: documentsUnavailableReason,
  load: loadCaseDocuments,
  rotateDocument,
} = useCaseDocuments();
// '' means closed — DocumentViewerOverlay renders nothing when its fileId
// doesn't resolve to a viewable document, so an empty string doubles as
// the closed state without a separate boolean.
const viewerFileId = ref('');
const recordStatuses = ref<RecordStatuses>({});
const recordNotes = ref<Record<string, string>>({});
const focusedRecordIndex = ref(0);
// v4's full-pane "reviewing" mode — replaces the center pane's normal
// hit-detail content (rail and tool panel both stay visible, same as
// identity/note's old center-pane takeover before Stage 6c inlined them).
// Reviews whichever record is focused, so arrow-key/j-k navigation and
// this pane's own Prev/Next buttons are the same underlying index.
const reviewing = ref(false);

// Mobile-only navigation state — desktop ignores all of it (it always
// shows hit detail, every mobile section unconditionally, and has no
// bottom sheet), enforced purely by Tailwind `lg:`/`hidden` classes in
// the template rather than a JS viewport check, so a live browser resize
// reflows instantly with no remount.
const mobileView = ref<'list' | 'hit'>('list');
const mobileTab = ref<'assess' | 'identity' | 'records'>('assess');
const mobileFilter = ref<HitFilterKey>('undecided');
// HitRail's own filter, lifted up here (rather than kept as HitRail-local
// state) so ArrowLeft/ArrowRight hit navigation can walk the same filtered
// list a reviewer is actually looking at, instead of silently including
// hits the active filter is hiding.
const desktopFilter = ref<HitFilterKey>('undecided');
const sheetOpen = ref(false);
const detailsSheetOpen = ref(false);

const toolPanelRef = ref<InstanceType<typeof ToolPanel>>();
const noteBarRef = ref<InstanceType<typeof HitNoteBar>>();
const documentViewerRef = ref<InstanceType<typeof DocumentViewerOverlay>>();

const hits = computed(() => caseDetail.value?.hits ?? []);
const selectedHitId = ref('');
const selectedHit = computed(
  () => hits.value.find((hit) => hit.id === selectedHitId.value) ?? hits.value[0]
);
const selectedRecords = computed(() => selectedHit.value?.records ?? []);
const focusedRecord = computed(() => selectedRecords.value[focusedRecordIndex.value]);

watch(selectedHitId, () => {
  reviewing.value = false;
  focusedRecordIndex.value = 0;
  mobileTab.value = 'assess';
});

function moveFocusedRecord(delta: number) {
  const n = selectedRecords.value.length;
  focusedRecordIndex.value = Math.max(0, Math.min(n - 1, focusedRecordIndex.value + delta));
}

function openRecordReview(recordId: string) {
  const index = selectedRecords.value.findIndex((record) => record.id === recordId);
  if (index >= 0) focusedRecordIndex.value = index;
  reviewing.value = true;
}

// Tapping a hit card on the mobile list screen both selects it (same as
// the desktop rail) and switches to the hit-detail "screen".
function openHitMobile(hitId: string) {
  selectedHitId.value = hitId;
  mobileView.value = 'hit';
}

// One step back at a time: the hit-detail screen steps back to the case's
// own hit list, which in turn leaves the case entirely — there was
// previously no way off the list screen except a browser-level back
// gesture, leaving a mobile reviewer stuck.
function backMobile() {
  if (mobileView.value === 'hit') {
    mobileView.value = 'list';
  } else {
    backToQueue();
  }
}

function recordCountLabel(hit: CaseHit): string {
  const n = hit.records?.length ?? 0;
  return n === 1 ? '1 record' : `${n} records`;
}

const mobileFilteredHits = computed(() =>
  hits.value.filter((hit) => mobileFilter.value === 'all' || !decidedHitIds.value.includes(hit.id))
);

const desktopFilteredHits = computed(() =>
  hits.value.filter((hit) => desktopFilter.value === 'all' || !decidedHitIds.value.includes(hit.id))
);

const showNoUndecidedHits = computed(
  () =>
    mobileView.value !== 'hit' && hits.value.length > 0 && desktopFilteredHits.value.length === 0
);

const mobileTabs = computed(() => [
  { key: 'assess' as const, label: 'Assessment' },
  { key: 'identity' as const, label: 'Identity' },
  { key: 'records' as const, label: `Records ${selectedRecords.value.length}` },
]);

// Swiping the hit-detail body steps between Assessment/Identity/Records —
// clamped at each end rather than wrapping, matching how the tab buttons
// themselves behave (tapping the first tab again doesn't jump to the last).
function stepMobileTab(delta: number) {
  const order = mobileTabs.value.map((t) => t.key);
  const index = order.indexOf(mobileTab.value);
  mobileTab.value = order[Math.max(0, Math.min(order.length - 1, index + delta))];
}
const { onTouchStart: onMobileTabTouchStart, onTouchEnd: onMobileTabTouchEnd } = useSwipeNavigation(
  () => stepMobileTab(1),
  () => stepMobileTab(-1)
);

const mobileHeaderTitle = computed(() =>
  mobileView.value === 'list' ? subjectName.value : (selectedHit.value?.matchedName ?? '')
);
// Deliberately short on the list screen — partner/region/externalId/decided
// count are all real and already shown in full by the case summary card
// just below (caseDetail.amlReviewId, subjectName, partnerName, region,
// decidedCount/totalCount), so repeating them here only wrapped MobileTopBar
// onto four lines for no new information.
const mobileHeaderSubtitle = computed(() =>
  mobileView.value === 'list'
    ? `${caseDetail.value?.amlReviewId ?? ''} · ${totalCount.value} hits`
    : (selectedHit.value?.id ?? '')
);

const casePct = computed(() =>
  totalCount.value === 0 ? 0 : Math.round((decidedCount.value / totalCount.value) * 100)
);

const mobileVerdictChipStyles: Record<HitVerdict, string> = {
  true_positive: 'bg-red-50 text-red-700',
  false_positive: 'bg-green-50 text-green-700',
  unknown: 'bg-amber-50 text-amber-700',
};
const mobileVerdictChipLabels: Record<HitVerdict, string> = {
  true_positive: 'True',
  false_positive: 'False',
  unknown: 'Unknown',
};

function initialSelectedHitId(): string {
  return (
    hits.value.find((hit) => !decidedHitIds.value.includes(hit.id))?.id ?? hits.value[0]?.id ?? ''
  );
}

onMounted(async () => {
  const amlReviewId = String(route.params.id);
  const client = useImmersveApiClient();

  loadedAmlReviewId.value = amlReviewId;
  sweepRecordState();
  const savedState = loadRecordState(amlReviewId);
  recordNotes.value = savedState.notes;
  recordStatuses.value = savedState.statuses;

  try {
    const review = await client.getAmlReviewById(amlReviewId);
    const results = await client.getAmlReviewResults(amlReviewId);
    const result = pickLatestAgentReview(results);
    agentReviewResultId.value = result?.id ?? null;

    const humanReview = pickLatestHumanReview(results);
    if (humanReview) {
      humanReviewResultId.value = humanReview.id;
      humanReviewModifiedAt.value = humanReview.modifiedAt ?? humanReview.createdAt;
      humanReviewLastReviewedBy.value = humanReview.lastReviewedBy ?? null;
      humanReviewCompletedAt.value = humanReview.completedAt ?? null;
      for (const verdict of humanReview.verdicts ?? []) {
        overrides.value[verdict.hitId] = verdict.verdict as HitVerdict;
        if (verdict.reasoning) comments.value[verdict.hitId] = verdict.reasoning;
      }
      reviewerSummary.value = humanReview.summary || '';
      submitted.value = humanReview.status === 'completed';
    }

    loadCaseDocuments(client, review);

    const identityDetails = resolveIdentityDetails(review);
    const enrichment = await resolveHitEnrichment(client, review, identityDetails);
    if (enrichment.status === 'unavailable') {
      enrichmentUnavailableReason.value = enrichment.reason;
    }
    caseDetail.value = mapAmlReviewToCase(
      review,
      result,
      enrichment.status === 'ok' ? enrichment.hitsById : undefined,
      { env: config.env, testModeEnabled: config.testModeEnabled }
    );
    selectedHitId.value = initialSelectedHitId();
  } catch (err) {
    error.value = err as Error;
  } finally {
    loading.value = false;
  }
});

onBeforeRouteLeave(() => {
  if (!dirty.value) return true;
  return window.confirm('You have unsaved changes. Leave without saving?');
});

function warnOnUnsavedUnload(event: BeforeUnloadEvent) {
  if (!dirty.value) return;
  event.preventDefault();
}
window.addEventListener('beforeunload', warnOnUnsavedUnload);
onUnmounted(() => window.removeEventListener('beforeunload', warnOnUnsavedUnload));

function persistRecordState() {
  if (!loadedAmlReviewId.value) return;
  saveRecordState(loadedAmlReviewId.value, {
    notes: recordNotes.value,
    statuses: recordStatuses.value,
  });
}

function cycleRecordStatus(recordId: string) {
  recordStatuses.value = {
    ...recordStatuses.value,
    [recordId]: nextRecordStatus(recordStatuses.value[recordId]),
  };
  persistRecordState();
}

function updateRecordNote(recordId: string, value: string) {
  recordNotes.value = { ...recordNotes.value, [recordId]: value };
  persistRecordState();
}

const subjectName = computed(() =>
  caseDetail.value ? fullName(caseDetail.value.identityDetails.name) : ''
);

const subjectSelfieDocument = computed(() =>
  caseDocuments.value.find((doc) => doc.type === 'self-image' && doc.state === 'ready')
);
const subjectPhotoUrl = computed(() => subjectSelfieDocument.value?.objectUrl);
// Its fileId, so clicking the photo can open the same DocumentViewerOverlay
// the Documents gallery uses rather than a second one-off lightbox.
const subjectPhotoFileId = computed(() => subjectSelfieDocument.value?.fileId);

function openDocumentViewer(fileId: string) {
  viewerFileId.value = fileId;
}

// A hit counts as "cleared" either because the bot already resolved it
// (false positive / whitelisted, per hitResolution's amlCaseHitsResolved
// rule) or because a reviewer has recorded an outcome — not because every
// hit needs a fresh human click, which is 2a's own mock behaviour but would
// make "ready" unreachable for any bot-resolved hit. HitRail's own
// All hits/Undecided filter and progress count use this same definition
// (passed down as `decidedIds`), so they never disagree with "Cleared"
// in the app bar about which hits still need attention.
const decidedHitIds = computed(() =>
  hits.value.filter((hit) => overrides.value[hit.id] || isHitResolved(hit)).map((hit) => hit.id)
);
const decidedCount = computed(() => decidedHitIds.value.length);
const totalCount = computed(() => hits.value.length);
const ready = computed(() => totalCount.value > 0 && decidedCount.value === totalCount.value);

const internalEscalationSummary = computed(() =>
  buildInternalEscalationSummary(hits.value, overrides.value, comments.value, reviewerSummary.value)
);
const partnerEscalationSummary = computed(() =>
  buildPartnerEscalationSummary(hits.value, overrides.value, comments.value, reviewerSummary.value)
);

// Selecting the verdict a hit already has (by click or by its T/F/U
// shortcut) clears the override rather than re-setting it — the only way
// to record a decision is to actively hold one, so a reviewer can always
// back out of a verdict they didn't mean to set. This never touches
// selectedHitId or triggers a save; the reviewer navigates and saves
// explicitly, this only ever updates the one hit's override in place.
function selectVerdict(hitId: string, verdict: HitVerdict) {
  const next = { ...overrides.value };
  if (next[hitId] === verdict) {
    delete next[hitId];
  } else {
    next[hitId] = verdict;
  }
  overrides.value = next;
  dirty.value = true;
}

function updateComment(hitId: string, value: string) {
  comments.value = { ...comments.value, [hitId]: value };
  dirty.value = true;
}

function updateReviewerSummary(value: string) {
  reviewerSummary.value = value;
  dirty.value = true;
}

const SUBMISSION_RESULT_MESSAGES: Record<AmlResultSubmissionStatus, string> = {
  approved: 'Submitted - applicant approved at the provider',
  withheld: 'Submitted - applicant withheld at the provider',
  skipped: 'Submitted - the result was not applied',
  untouched: 'Submitted - the provider profile was left unchanged',
};

function describeSubmissionOutcome(outcome: CompleteAmlReviewResponse): string {
  const result = SUBMISSION_RESULT_MESSAGES[outcome.amlResultSubmissionStatus];
  const hitReviews = outcome.amlHitReviewSubmissionStatus;
  return hitReviews.startsWith('skipped')
    ? `${result} (hit reviews ${hitReviews.replace('skipped-', 'skipped: ')})`
    : result;
}

// amethyst's stable errorCode discriminators (amethyst PR #6830) for a 403
// on save/submit — lets these say what actually went wrong instead of
// guessing between "already finalized" and "no permission" every time.
const FORBIDDEN_ERROR_CODE_MESSAGES: Record<string, (action: 'save' | 'submit') => string> = {
  AML_REVIEW_FINALIZED: (action) =>
    `This case can't be ${action === 'submit' ? 'submitted' : 'saved'} — it has already been finalized.`,
  AML_REVIEW_RESULT_NOT_AGENT_REVIEW: () =>
    "This case can't be saved — its review record no longer matches what was loaded. Please reload the page.",
  AML_REVIEW_RESULT_NOT_HUMAN_REVIEW: () =>
    "This case can't be saved — its review record no longer matches what was loaded. Please reload the page.",
};

function describeWriteError(err: unknown, action: 'save' | 'submit'): string {
  const cause = (err as { cause?: unknown } | undefined)?.cause;
  const status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
  if (status === 400) {
    return 'This case was updated elsewhere since it was loaded. Reloading the latest draft — please redo any changes since then and try again.';
  }
  if (status === 403) {
    const errorCode = axios.isAxiosError(cause)
      ? (cause.response?.data as { errorCode?: string } | undefined)?.errorCode
      : undefined;
    const knownMessage = errorCode && FORBIDDEN_ERROR_CODE_MESSAGES[errorCode];
    if (knownMessage) return knownMessage(action);
    return `This case can't be ${action === 'submit' ? 'submitted' : 'saved'} — you may not have permission.`;
  }
  return (err as Error).message;
}

function humanVerdictsPayload() {
  return Object.entries(overrides.value).map(([hitId, verdict]) => ({
    hitId,
    verdict,
    reasoning: comments.value[hitId] || undefined,
  }));
}

async function save({ silent = false }: { silent?: boolean } = {}) {
  if (submitted.value) return;
  if (!agentReviewResultId.value) {
    saveError.value = "This case can't be saved — missing agent review.";
    return;
  }
  if (!reviewerEmail.value) {
    saveError.value = 'Set a reviewer email at /config before saving.';
    return;
  }
  saving.value = true;
  saveError.value = null;
  try {
    const client = useImmersveApiClient();
    if (!humanReviewResultId.value) {
      const created = await client.createHumanReview(agentReviewResultId.value, {
        amlHits: humanVerdictsPayload(),
        summary: reviewerSummary.value || undefined,
        reviewedBy: reviewerEmail.value,
      });
      humanReviewResultId.value = created.id;
      humanReviewModifiedAt.value = created.modifiedAt ?? created.createdAt;
      humanReviewLastReviewedBy.value = created.lastReviewedBy ?? reviewerEmail.value;
    } else if (humanReviewModifiedAt.value) {
      const updated = await client.updateHumanReview(humanReviewResultId.value, {
        amlHits: humanVerdictsPayload(),
        summary: reviewerSummary.value || undefined,
        reviewedBy: reviewerEmail.value,
        lastModifiedAt: humanReviewModifiedAt.value,
      });
      humanReviewModifiedAt.value = updated.modifiedAt ?? updated.createdAt;
      humanReviewLastReviewedBy.value = updated.lastReviewedBy ?? reviewerEmail.value;
    }
    dirty.value = false;
    if (!silent) flashSaveBanner('Saved');
  } catch (err) {
    saveError.value = describeWriteError(err, 'save');
    const cause = (err as { cause?: unknown } | undefined)?.cause;
    if (axios.isAxiosError(cause) && cause.response?.status === 400 && humanReviewResultId.value) {
      const client = useImmersveApiClient();
      const latest = await client.getLatestHumanReview(caseDetail.value?.amlReviewId ?? '');
      if (latest) humanReviewModifiedAt.value = latest.modifiedAt ?? latest.createdAt;
    }
  } finally {
    saving.value = false;
  }
}

async function submit() {
  if (!ready.value || submitted.value || !caseDetail.value) return;
  if (!reviewerEmail.value) {
    submitError.value = 'Set a reviewer email at /config before submitting.';
    return;
  }
  submitting.value = true;
  submitError.value = null;
  await save({ silent: true });
  if (saveError.value) {
    submitting.value = false;
    return;
  }
  try {
    const client = useImmersveApiClient();
    const outcome = await client.completeAmlReview(
      caseDetail.value.amlReviewId,
      reviewerEmail.value
    );
    submitted.value = true;
    flashSaveBanner(describeSubmissionOutcome(outcome));
    const completed = await client.getLatestHumanReview(caseDetail.value.amlReviewId);
    if (completed) {
      humanReviewCompletedAt.value = completed.completedAt ?? null;
      humanReviewModifiedAt.value = completed.modifiedAt ?? completed.createdAt;
    }
  } catch (err) {
    submitError.value = describeWriteError(err, 'submit');
  } finally {
    submitting.value = false;
  }
}

useAmlWorkbenchShortcuts({
  hits: () => desktopFilteredHits.value,
  selectedHitId: () => selectedHitId.value,
  selectHit: (id) => {
    selectedHitId.value = id;
  },
  records: () => selectedRecords.value,
  focusedRecordIndex: () => focusedRecordIndex.value,
  setFocusedRecordIndex: (index) => {
    focusedRecordIndex.value = index;
  },
  selectVerdict,
  cycleRecordStatus,
  openReader: openRecordReview,
  setToolByIndex: (index) => toolPanelRef.value?.setToolByIndex(index),
  openNoteComposer: () => noteBarRef.value?.openComposer(),
  cancelNoteComposer: () => noteBarRef.value?.closeComposer(),
  togglePane: () => {
    rightCollapsed.value = !rightCollapsed.value;
  },
  switchPaneTab: () => toolPanelRef.value?.toggleTab(),
  openShortcuts: () => {
    shortcutsOpen.value = true;
  },
  save,
  submit,
  kbEnabled: () => kbEnabled.value,
  isOverlayOpen: () => shortcutsOpen.value || !!viewerFileId.value,
  closeOverlay: () => {
    shortcutsOpen.value = false;
    viewerFileId.value = '';
  },
  openDocumentViewer: () => {
    // Any ready document, not just images — PDFs get a full preview too.
    const first = caseDocuments.value.find((doc) => doc.state === 'ready');
    if (first) viewerFileId.value = first.fileId;
  },
  isDocumentViewerOpen: () => !!viewerFileId.value,
  stepDocumentViewer: (delta) => documentViewerRef.value?.step(delta),
  rotateDocumentViewer: (delta) => documentViewerRef.value?.rotate(delta),
  isReviewing: () => reviewing.value,
  closeReview: () => {
    reviewing.value = false;
  },
});
</script>

<template>
  <div class="flex h-screen w-screen flex-col bg-white" data-test="screen-aml-review-case">
    <!-- Loading/error has no case data to feed the full app bar, so this is
         a skeleton keeping the one thing a stuck reviewer needs — a way back
         to the queue — shaped exactly like whichever real bar (MobileTopBar
         or CaseStatusBar) replaces it once caseDetail loads, so that swap
         only fades in content rather than snapping the whole bar's height,
         padding and back-button style to something different. -->
    <template v-if="loading || error">
      <div
        class="flex flex-none items-center gap-2.5 border-b border-gray-200 bg-white px-3 py-2.5 lg:hidden"
      >
        <button
          type="button"
          aria-label="Back to queue"
          data-test="btn-back-to-queue-skeleton"
          class="flex h-9 w-9 flex-none items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
          @click="backToQueue"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 4 6 10l6 6" />
          </svg>
        </button>
      </div>
      <div
        class="hidden min-h-14 flex-none items-center gap-3.5 border-b border-gray-200 bg-white px-4 py-2 lg:flex"
      >
        <button
          type="button"
          aria-label="Back to queue"
          data-test="btn-back-to-queue-skeleton"
          class="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50"
          @click="backToQueue"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 4 6 10l6 6" />
          </svg>
        </button>
      </div>
    </template>

    <p v-if="loading" data-test="case-loading" class="p-6 text-sm text-gray-500">Loading…</p>
    <p v-else-if="error" data-test="case-error" class="p-6 text-sm text-red-600">
      {{ error.message }}
    </p>

    <template v-else-if="caseDetail">
      <MobileTopBar
        class="lg:hidden"
        :title="mobileHeaderTitle"
        :subtitle="mobileHeaderSubtitle"
        :show-back="true"
        :ready="ready"
        :submitted="submitted"
        :submitting="submitting"
        @back="backMobile"
        @submit="submit"
        @open-tools="sheetOpen = true"
        @open-details="detailsSheetOpen = true"
      />
      <CaseStatusBar
        :name="subjectName"
        :case-id="caseDetail.amlReviewId"
        :provider="caseDetail.kycProvider"
        :provider-case-url="caseDetail.providerCaseUrl"
        :zendesk-case-url="caseDetail.zendeskCaseUrl"
        :internal-escalation-summary="internalEscalationSummary"
        :partner-escalation-summary="partnerEscalationSummary"
        :partner="caseDetail.partnerName"
        :region="caseDetail.region"
        :external-check-id="caseDetail.externalCheckId"
        :account-id="caseDetail.accountId"
        :created-at="caseDetail.createdAt"
        :last-saved-at="humanReviewModifiedAt"
        :last-reviewed-by="humanReviewLastReviewedBy"
        :completed-at="humanReviewCompletedAt"
        :superseding-aml-review-id="caseDetail.supersedingAmlReviewId"
        :external-id="caseDetail.externalProfileId"
        :ready="ready"
        :submitted="submitted"
        :dirty="dirty"
        :saving="saving"
        :submitting="submitting"
        :kb-enabled="kbEnabled"
        :pane-visible="!rightCollapsed"
        @back="backToQueue"
        @save="save"
        @submit="submit"
        @toggle-kb="kbEnabled = !kbEnabled"
        @open-shortcuts="shortcutsOpen = true"
        @toggle-pane="rightCollapsed = !rightCollapsed"
      />

      <p
        v-if="saveError || submitError"
        data-test="case-save-error"
        class="flex-none border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700"
      >
        {{ submitError || saveError }}
      </p>
      <p
        v-else-if="saveBanner"
        data-test="save-banner"
        class="flex-none border-b border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700"
      >
        {{ saveBanner }}
      </p>

      <div class="flex min-h-0 flex-1">
        <HitRail
          v-model:filter="desktopFilter"
          :hits="hits"
          :selected-id="selectedHit?.id ?? ''"
          :decisions="overrides"
          :notes="comments"
          :decided-ids="decidedHitIds"
          @select="selectedHitId = $event"
        />

        <div class="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50">
          <!-- v4's full-pane "reviewing" mode takes over this whole column
               (hit detail + verdict bar together) rather than being a
               separate overlay — the rail and tool panel on either side
               stay visible, same reasoning Stage 6c already applied to
               identity/notes before inlining them for good. Shared as-is
               between mobile and desktop, matching v4's own unconditional
               `reviewing` block. -->
          <RecordReviewPane
            v-if="reviewing && focusedRecord"
            :record="focusedRecord"
            :note="recordNotes[focusedRecord.id]"
            :status="recordStatuses[focusedRecord.id]"
            :can-prev="focusedRecordIndex > 0"
            :can-next="focusedRecordIndex < selectedRecords.length - 1"
            :kb-visible="kbEnabled"
            @close="reviewing = false"
            @prev="moveFocusedRecord(-1)"
            @next="moveFocusedRecord(1)"
            @cycle-status="cycleRecordStatus(focusedRecord.id)"
            @update-note="updateRecordNote"
          />
          <template v-else>
            <!-- Mobile-only "list" screen: case summary + All hits/Undecided
                 filter + a row per hit, tapping into the hit-detail screen.
                 Desktop never shows this — the persistent HitRail above
                 covers the same ground there. -->
            <div
              v-if="mobileView === 'list'"
              class="min-h-0 flex-1 overflow-y-auto p-3 lg:hidden"
              data-test="mobile-list-screen"
            >
              <div class="flex flex-col gap-3">
                <div class="rounded-xl border border-gray-200 bg-white p-3.5 shadow-sm">
                  <div class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">
                    {{ caseDetail.amlReviewId }}
                  </div>
                  <div class="mt-1.5 text-lg font-bold text-gray-900">{{ subjectName }}</div>
                  <div class="mt-1 text-xs text-gray-500">
                    {{ caseDetail.partnerName }} · {{ caseDetail.region }}
                  </div>
                  <div class="mt-3 flex gap-2">
                    <a
                      :href="caseDetail.providerCaseUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-test="mobile-case-provider-link"
                      class="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-indigo-tint-border)] bg-[var(--color-indigo-tint-bg)] py-2.5 text-[13px] font-semibold text-[var(--color-indigo-tint-text)]"
                    >
                      {{ caseDetail.kycProvider === 'frankieOne' ? 'FrankieOne' : 'Sumsub' }}
                      <ArrowTopRightOnSquareIcon class="size-3.5" />
                    </a>
                    <a
                      v-if="caseDetail.zendeskCaseUrl"
                      :href="caseDetail.zendeskCaseUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-test="mobile-case-zendesk-link"
                      class="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2.5 text-[13px] font-semibold text-gray-700"
                    >
                      Zendesk
                      <ArrowTopRightOnSquareIcon class="size-3.5" />
                    </a>
                  </div>
                  <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
                    <div
                      class="h-full rounded-full bg-indigo-600 transition-[width]"
                      :style="{ width: `${casePct}%` }"
                    ></div>
                  </div>
                  <div class="mt-2 flex items-center justify-between text-xs">
                    <span class="font-medium text-gray-700"
                      >{{ decidedCount }}/{{ totalCount }} decided</span
                    >
                    <span class="text-gray-400">{{ totalCount - decidedCount }} left</span>
                  </div>
                </div>

                <CaseSummaryCard
                  :recommendation="caseDetail.recommendation"
                  :summary="caseDetail.summary"
                />
                <ReviewerSummaryCard
                  :summary="reviewerSummary"
                  :total-hits="totalCount"
                  @save="updateReviewerSummary"
                />

                <HitFilterTabs v-model="mobileFilter" />

                <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <button
                    v-for="hit in mobileFilteredHits"
                    :key="hit.id"
                    type="button"
                    data-test="mobile-hit-row"
                    class="flex w-full items-center gap-3 border-b border-gray-100 p-3.5 text-left last:border-0"
                    @click="openHitMobile(hit.id)"
                  >
                    <span class="min-w-0 flex-1">
                      <span class="flex items-center gap-1.5">
                        <HitCategoryTag v-if="hit.hitTypes[0]" :hit-type="hit.hitTypes[0]" />
                        <span
                          v-if="overrides[hit.id]"
                          class="inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          :class="mobileVerdictChipStyles[overrides[hit.id]]"
                        >
                          {{ mobileVerdictChipLabels[overrides[hit.id]] }}
                        </span>
                      </span>
                      <span class="mt-1 block truncate text-[15px] font-semibold text-gray-900">{{
                        hit.matchedName
                      }}</span>
                      <span class="mt-0.5 block text-xs text-gray-500">{{
                        recordCountLabel(hit)
                      }}</span>
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="flex-none text-gray-400"
                    >
                      <path d="m8 4 6 6-6 6" />
                    </svg>
                  </button>
                  <p
                    v-if="mobileFilteredHits.length === 0"
                    class="p-4 text-center text-xs text-gray-400"
                  >
                    No hits match this filter.
                  </p>
                </div>
              </div>
            </div>

            <div
              class="min-h-0 flex-1 overflow-y-auto p-4"
              :class="mobileView === 'list' ? 'hidden lg:block' : 'block'"
              :data-scroll-container="mobileView !== 'list'"
              data-test="hit-detail-body"
              @touchstart="onMobileTabTouchStart"
              @touchend="onMobileTabTouchEnd"
            >
              <div v-if="selectedHit" class="flex flex-col gap-3">
                <!-- Case-wide, not per-hit — sits above the per-hit tabs so
                     it stays visible regardless of which one is active.
                     Real AmlReviewResult fields (recommendation/summary),
                     restored per direct feedback after the v4 rebuild
                     dropped them for having no case-level surface left to
                     render them in. Desktop-only from here — on mobile it
                     lives on the case's own list screen instead (below),
                     matching the design's "case + hit list" grouping rather
                     than repeating per hit. -->
                <CaseSummaryCard
                  class="hidden lg:block"
                  :recommendation="caseDetail.recommendation"
                  :summary="caseDetail.summary"
                />
                <ReviewerSummaryCard
                  class="hidden lg:block"
                  :summary="reviewerSummary"
                  :total-hits="totalCount"
                  @save="updateReviewerSummary"
                />

                <section
                  v-if="showNoUndecidedHits"
                  data-test="no-undecided-hits"
                  class="hidden rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm lg:block"
                >
                  <p class="text-sm font-semibold text-gray-900">No hits need review</p>
                  <p class="mt-1 text-xs text-gray-500">
                    Every hit on this case has already been decided.
                  </p>
                  <button
                    type="button"
                    class="mt-3 rounded-full border border-gray-300 bg-white px-3 py-1 text-[11.5px] font-semibold text-gray-500 hover:bg-gray-50"
                    @click="desktopFilter = 'all'"
                  >
                    View all hits
                  </button>
                </section>

                <template v-else>
                  <div class="flex gap-1 rounded-lg bg-gray-100 p-1 lg:hidden">
                    <button
                      v-for="t in mobileTabs"
                      :key="t.key"
                      type="button"
                      class="flex-1 rounded-md py-1.5 text-xs font-semibold"
                      :class="
                        mobileTab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                      "
                      @click="mobileTab = t.key"
                    >
                      {{ t.label }}
                    </button>
                  </div>

                  <section
                    class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                    :class="mobileTab === 'assess' ? '' : 'hidden lg:block'"
                  >
                    <div class="border-b border-gray-100 p-4">
                      <div class="flex items-start justify-between gap-3">
                        <span class="flex items-center gap-1 text-[11px] font-medium text-gray-400">
                          {{ selectedHit.id }}
                          <CopyButton :value="selectedHit.id" label="hit ID" />
                        </span>
                        <div class="flex flex-wrap justify-end gap-1.5">
                          <HitCategoryTag
                            v-for="hitType in selectedHit.hitTypes"
                            :key="hitType"
                            :hit-type="hitType"
                          />
                        </div>
                      </div>
                      <h2 class="mt-1 text-lg font-bold break-words text-gray-900">
                        {{ selectedHit.matchedName }}
                      </h2>
                    </div>
                    <AgentAssessmentBox
                      v-if="selectedHit.botReasoning"
                      :reasoning="selectedHit.botReasoning"
                      :tone="selectedHit.verdict"
                      :reviewer-verdict="overrides[selectedHit.id]"
                      :evidence="selectedHit.evidence"
                    />
                  </section>

                  <!-- The real Subject-vs-Matched comparison, sourced from the
                     same fields FrankieOne's own "Key Data" panel shows for this
                     match (Full name/AKA/Date of birth/Nationality/Associates) —
                     confirmed directly against the live portal, not the
                     AML MATCH COMMENT prose. Per-hit, not per-record: no
                     individual hit record carries its own comparison. Always
                     inline, matching v4's own unconditional rendering with no
                     collapse control. -->
                  <IdentityComparison
                    v-if="selectedHit.diff?.length"
                    :class="mobileTab === 'identity' ? '' : 'hidden lg:block'"
                    :subject-name="subjectName"
                    :matched-name="selectedHit.matchedName"
                    :diff="selectedHit.diff"
                    :subject-photo-url="subjectPhotoUrl"
                    :subject-photo-file-id="subjectPhotoFileId"
                    @open="openDocumentViewer"
                  />

                  <HitRecordList
                    v-if="selectedRecords.length"
                    :class="mobileTab === 'records' ? '' : 'hidden lg:block'"
                    :records="selectedRecords"
                    :statuses="recordStatuses"
                    :notes="recordNotes"
                    :focused-id="focusedRecord?.id"
                    :kb-enabled="kbEnabled"
                    @cycle-status="cycleRecordStatus"
                    @update-note="updateRecordNote"
                    @open-reader="openRecordReview"
                  />
                  <p
                    v-else-if="enrichmentUnavailableReason"
                    data-test="enrichment-unavailable"
                    class="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
                  >
                    Provider records unavailable — {{ enrichmentUnavailableReason }}
                  </p>

                  <section
                    v-if="comments[selectedHit.id]"
                    class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div class="flex items-center gap-1">
                      <h3 class="text-sm font-semibold text-gray-900">Reviewer note</h3>
                      <CopyButton
                        class="ml-auto"
                        :value="comments[selectedHit.id] ?? ''"
                        label="reviewer note"
                      />
                    </div>
                    <p class="mt-2 text-sm break-words whitespace-pre-wrap text-gray-700">
                      {{ comments[selectedHit.id] }}
                    </p>
                  </section>
                </template>
              </div>

              <p v-else class="text-sm text-gray-500">No hits recorded for this case.</p>
            </div>

            <HitNoteBar
              v-if="selectedHit && !showNoUndecidedHits"
              ref="noteBarRef"
              :class="mobileView === 'list' ? 'hidden lg:flex lg:flex-col' : ''"
              :hit-id="selectedHit.id"
              :comment="comments[selectedHit.id]"
              :override="overrides[selectedHit.id]"
              :kb-enabled="kbEnabled"
              @select-verdict="selectVerdict"
              @update-comment="updateComment"
            />
          </template>
        </div>

        <ToolPanel
          v-if="!rightCollapsed"
          ref="toolPanelRef"
          :subject-name="subjectName"
          :subject-address="caseDetail.identityDetails.address"
          :focused-record-url="focusedRecord?.url"
          :review-region="caseDetail.region"
          :account-id="caseDetail.accountId"
          :kb-enabled="kbEnabled"
        >
          <template #footer>
            <CaseDocumentsPanel
              variant="panel"
              :kb-enabled="kbEnabled"
              :documents="caseDocuments"
              :unavailable-reason="documentsUnavailableReason"
              @open="openDocumentViewer"
            />
          </template>
        </ToolPanel>
      </div>

      <MobileBottomSheet :open="sheetOpen" @close="sheetOpen = false">
        <ToolPanel
          variant="sheet"
          :subject-name="subjectName"
          :subject-address="caseDetail.identityDetails.address"
          :focused-record-url="focusedRecord?.url"
          :review-region="caseDetail.region"
          :account-id="caseDetail.accountId"
          :documents-count="caseDocuments.length"
        >
          <template #documents>
            <CaseDocumentsPanel
              variant="sheet"
              :documents="caseDocuments"
              :unavailable-reason="documentsUnavailableReason"
              @open="openDocumentViewer"
            />
          </template>
        </ToolPanel>
      </MobileBottomSheet>

      <MobileBottomSheet :open="detailsSheetOpen" @close="detailsSheetOpen = false">
        <div class="min-h-0 flex-1 overflow-y-auto">
          <CaseIdsPanel
            :case-id="caseDetail.amlReviewId"
            :account-id="caseDetail.accountId"
            :external-id="caseDetail.externalProfileId"
            :external-check-id="caseDetail.externalCheckId"
            :created-at="caseDetail.createdAt"
            :last-saved-at="humanReviewModifiedAt"
            :last-reviewed-by="humanReviewLastReviewedBy"
            :completed-at="humanReviewCompletedAt"
            :superseding-aml-review-id="caseDetail.supersedingAmlReviewId"
          />
        </div>
      </MobileBottomSheet>
    </template>
  </div>

  <ShortcutsModal
    v-if="shortcutsOpen"
    :kb-enabled="kbEnabled"
    @close="shortcutsOpen = false"
    @toggle-kb="kbEnabled = !kbEnabled"
  />

  <DocumentViewerOverlay
    v-if="viewerFileId"
    ref="documentViewerRef"
    :documents="caseDocuments"
    :file-id="viewerFileId"
    @close="viewerFileId = ''"
    @update:file-id="viewerFileId = $event"
    @rotate="rotateDocument"
  />
</template>
